import React from 'react';
import { Container } from 'react-bootstrap';
import Navbar from './Navbar';
import Footer from './Footer';

const Terms: React.FC = () => {
  return (
    <>
      <Navbar />
      <Container className="py-5">
        <h1 className="mb-4">Terms of Service</h1>
        <p className="text-muted">Last updated: {new Date().toLocaleDateString()}</p>

        <section className="my-4">
          <h2>1. Acceptance of Terms</h2>
          <p>
            These Terms of Service ("Terms") constitute a legally binding agreement between you ("User," "you," or "your")
            and Defined Home Services LLC ("Company," "we," "us," or "our") governing your access to and use of the DHS CRM
            platform, including any related services, features, content, and applications (collectively, the "Service").
          </p>
          <p>
            By accessing, registering for, or using the Service, you acknowledge that you have read, understood, and agree to
            be bound by these Terms and our Privacy Policy, which is incorporated herein by reference. If you do not agree to
            these Terms, you must not access or use the Service.
          </p>
          <p>
            You represent and warrant that: (a) you have the legal capacity and authority to enter into these Terms; (b) if
            you are entering into these Terms on behalf of an organization, you have the authority to bind that organization
            to these Terms; and (c) you are at least 18 years of age.
          </p>
        </section>

        <section className="my-4">
          <h2>2. Definitions</h2>
          <p>For purposes of these Terms:</p>
          <ul>
            <li><strong>"Account"</strong> means the user account created to access and use the Service.</li>
            <li><strong>"Customer Data"</strong> means all data, content, and materials that you upload, submit, or transmit through the Service.</li>
            <li><strong>"Documentation"</strong> means the user guides, technical specifications, and other documentation provided by us.</li>
            <li><strong>"Subscription Plan"</strong> means the specific service tier you have selected and paid for.</li>
            <li><strong>"User"</strong> includes both individual users and any organization on whose behalf they are acting.</li>
          </ul>
        </section>

        <section className="my-4">
          <h2>3. Service Description and Modifications</h2>
          <p>
            The Service is a customer relationship management (CRM) platform designed for home service businesses. We provide
            various features including customer management, appointment scheduling, invoicing, payment processing, and related
            business management tools.
          </p>
          <p>
            We reserve the right to modify, suspend, or discontinue any aspect of the Service at any time, with or without
            notice. We may also impose limits on certain features or restrict access to parts or all of the Service without
            notice or liability. We will make reasonable efforts to notify you of material changes that negatively impact
            your use of the Service.
          </p>
        </section>

        <section className="my-4">
          <h2>4. Account Registration and Security</h2>
          <p>
            To use the Service, you must create an Account by providing accurate, current, and complete information. You agree
            to maintain and promptly update your Account information to keep it accurate and complete.
          </p>
          <p>
            You are solely responsible for maintaining the confidentiality of your Account credentials (username and password)
            and for all activities that occur under your Account. You must immediately notify us of any unauthorized access to
            or use of your Account. We will not be liable for any loss or damage arising from your failure to protect your
            Account credentials.
          </p>
          <p>
            You may not: (a) share your Account credentials with any third party; (b) use another user's Account without
            permission; (c) create an Account using a false identity or information; or (d) create multiple Accounts for the
            same entity without our prior written consent.
          </p>
        </section>

        <section className="my-4">
          <h2>5. Subscription Plans and Billing</h2>
          <h3>5.1 Subscription Fees</h3>
          <p>
            Access to the Service requires a paid subscription. Subscription fees are based on the plan you select and are
            billed in advance on a monthly or annual basis, as specified during signup. All fees are non-refundable except
            as expressly stated in these Terms or required by law.
          </p>

          <h3>5.2 Payment Terms</h3>
          <p>
            You agree to provide current, complete, and accurate billing information. You authorize us to charge your designated
            payment method for all fees incurred. If automatic billing fails, we will attempt to notify you and provide a grace
            period to update payment information. Failure to pay may result in suspension or termination of your Account.
          </p>

          <h3>5.3 Price Changes</h3>
          <p>
            We may change our subscription fees at any time. We will provide at least 30 days' advance notice of any fee
            increases. Your continued use of the Service after the fee increase takes effect constitutes your acceptance of
            the new fees. If you do not agree to the fee increase, you may cancel your subscription.
          </p>

          <h3>5.4 Taxes</h3>
          <p>
            All fees are exclusive of applicable federal, state, local, and foreign taxes, duties, and similar assessments.
            You are responsible for all such taxes except for taxes based on our net income.
          </p>
        </section>

        <section className="my-4">
          <h2>6. Free Trial and Money-Back Guarantee</h2>
          <p>
            We may offer a free trial period for new users. Free trials are subject to these Terms and may be subject to
            additional conditions. We reserve the right to determine your eligibility for a free trial and to withdraw or
            modify free trial offers at any time.
          </p>
          <p>
            If we offer a money-back guarantee, the specific terms will be provided at the time of purchase. Refund requests
            must be submitted within the specified guarantee period and comply with the stated conditions.
          </p>
        </section>

        <section className="my-4">
          <h2>7. Acceptable Use Policy</h2>
          <p>You agree not to:</p>
          <ul>
            <li>Use the Service for any illegal, fraudulent, or unauthorized purpose</li>
            <li>Violate any applicable laws, regulations, or third-party rights</li>
            <li>Transmit any viruses, malware, or other harmful code</li>
            <li>Attempt to gain unauthorized access to the Service or related systems</li>
            <li>Interfere with or disrupt the Service or servers or networks connected to the Service</li>
            <li>Use automated systems (bots, scripts, scrapers) to access the Service without our permission</li>
            <li>Reverse engineer, decompile, or disassemble any part of the Service</li>
            <li>Remove, alter, or obscure any proprietary notices on the Service</li>
            <li>Use the Service to send spam, chain letters, or other unsolicited communications</li>
            <li>Collect or harvest user information without consent</li>
            <li>Impersonate any person or entity or misrepresent your affiliation with any person or entity</li>
            <li>Use the Service in any way that could damage our reputation or business</li>
          </ul>
        </section>

        <section className="my-4">
          <h2>8. Intellectual Property Rights</h2>
          <h3>8.1 Our Rights</h3>
          <p>
            The Service and all related technology, software, content, materials, and intellectual property are owned by us
            or our licensors and are protected by copyright, trademark, patent, and other intellectual property laws. These
            Terms do not grant you any ownership rights to the Service.
          </p>

          <h3>8.2 Limited License</h3>
          <p>
            Subject to your compliance with these Terms, we grant you a limited, non-exclusive, non-transferable,
            non-sublicensable, revocable license to access and use the Service solely for your internal business purposes
            in accordance with your Subscription Plan.
          </p>

          <h3>8.3 Your Data</h3>
          <p>
            You retain all ownership rights to your Customer Data. By using the Service, you grant us a worldwide,
            non-exclusive, royalty-free license to use, process, store, and display your Customer Data solely to provide
            and improve the Service. You represent and warrant that you have all necessary rights to grant this license.
          </p>
        </section>

        <section className="my-4">
          <h2>9. Customer Data and Privacy</h2>
          <p>
            You are solely responsible for the accuracy, quality, and legality of your Customer Data and the means by which
            you acquired it. You agree not to upload, transmit, or store any data that: (a) violates any law or regulation;
            (b) infringes any third-party rights; (c) contains viruses or harmful code; or (d) is defamatory, obscene, or
            objectionable.
          </p>
          <p>
            Our collection, use, and disclosure of personal information is governed by our Privacy Policy. We implement
            reasonable security measures to protect your data, but we cannot guarantee absolute security. You acknowledge
            that you provide your data at your own risk.
          </p>
          <p>
            We will comply with applicable data protection laws in our processing of Customer Data. You are responsible for
            complying with all applicable laws regarding your collection and use of customer information through the Service.
          </p>
        </section>

        <section className="my-4">
          <h2>10. Third-Party Services and Links</h2>
          <p>
            The Service may integrate with or contain links to third-party services, websites, or applications. These
            third-party services are not under our control, and we are not responsible for their content, privacy practices,
            or terms of service. Your use of third-party services is at your own risk and subject to their terms.
          </p>
          <p>
            Any integration with payment processors, communication services, or other third-party providers is subject to
            their respective terms and conditions. We are not liable for any issues arising from your use of such services.
          </p>
        </section>

        <section className="my-4">
          <h2>11. Service Availability and Support</h2>
          <p>
            We strive to maintain high availability of the Service but do not guarantee uninterrupted or error-free operation.
            The Service may be temporarily unavailable due to maintenance, updates, or circumstances beyond our control. We
            will make reasonable efforts to provide advance notice of scheduled maintenance.
          </p>
          <p>
            Technical support is provided as described in the Documentation and may vary based on your Subscription Plan.
            Support does not include services such as data migration, custom integrations, or training beyond standard
            documentation.
          </p>
        </section>

        <section className="my-4">
          <h2>12. Warranties and Disclaimers</h2>
          <p>
            THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS OR IMPLIED.
            TO THE FULLEST EXTENT PERMITTED BY LAW, WE DISCLAIM ALL WARRANTIES, INCLUDING IMPLIED WARRANTIES OF MERCHANTABILITY,
            FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, AND ANY WARRANTIES ARISING FROM COURSE OF DEALING OR USAGE OF TRADE.
          </p>
          <p>
            We do not warrant that: (a) the Service will meet your requirements; (b) the Service will be uninterrupted,
            timely, secure, or error-free; (c) the results obtained from using the Service will be accurate or reliable;
            or (d) any errors in the Service will be corrected.
          </p>
          <p>
            You acknowledge that we do not control the transfer of data over communications facilities and that the Service
            may be subject to limitations, delays, and other problems inherent in the use of such facilities.
          </p>
        </section>

        <section className="my-4">
          <h2>13. Limitation of Liability</h2>
          <p>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT SHALL WE, OUR AFFILIATES, OFFICERS, DIRECTORS, EMPLOYEES,
            AGENTS, OR LICENSORS BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES,
            INCLUDING WITHOUT LIMITATION DAMAGES FOR LOST PROFITS, LOST DATA, BUSINESS INTERRUPTION, OR ANY OTHER COMMERCIAL
            DAMAGES OR LOSSES, ARISING OUT OF OR RELATED TO YOUR USE OR INABILITY TO USE THE SERVICE, EVEN IF WE HAVE BEEN
            ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
          </p>
          <p>
            OUR TOTAL LIABILITY TO YOU FOR ALL CLAIMS ARISING OUT OF OR RELATED TO THESE TERMS OR THE SERVICE SHALL NOT
            EXCEED THE AMOUNT YOU PAID TO US FOR THE SERVICE IN THE TWELVE (12) MONTHS PRECEDING THE EVENT GIVING RISE TO
            THE CLAIM, OR ONE HUNDRED DOLLARS ($100), WHICHEVER IS GREATER.
          </p>
          <p>
            Some jurisdictions do not allow the limitation or exclusion of liability for incidental or consequential damages.
            In such jurisdictions, our liability will be limited to the greatest extent permitted by law.
          </p>
        </section>

        <section className="my-4">
          <h2>14. Indemnification</h2>
          <p>
            You agree to defend, indemnify, and hold harmless us, our affiliates, and our respective officers, directors,
            employees, contractors, agents, and representatives from and against any and all claims, damages, obligations,
            losses, liabilities, costs, and expenses (including reasonable attorneys' fees) arising from: (a) your use of
            the Service; (b) your violation of these Terms; (c) your violation of any third-party rights, including
            intellectual property, privacy, or other proprietary rights; or (d) your Customer Data.
          </p>
          <p>
            We reserve the right to assume the exclusive defense and control of any matter subject to indemnification by you,
            in which event you will cooperate in asserting any available defenses.
          </p>
        </section>

        <section className="my-4">
          <h2>15. Term and Termination</h2>
          <h3>15.1 Term</h3>
          <p>
            These Terms commence when you first access the Service and continue until terminated in accordance with this section.
          </p>

          <h3>15.2 Termination by You</h3>
          <p>
            You may cancel your subscription at any time through your Account settings or by contacting us. Cancellation
            will be effective at the end of your current billing period. You will not receive a refund for any fees already paid.
          </p>

          <h3>15.3 Termination by Us</h3>
          <p>
            We may suspend or terminate your access to the Service immediately, without prior notice or liability, for any
            reason, including if you breach these Terms. We may also terminate your Account if it remains inactive for an
            extended period.
          </p>

          <h3>15.4 Effect of Termination</h3>
          <p>
            Upon termination, your right to use the Service immediately ceases. We may delete your Account and Customer Data
            following termination. You should export any data you wish to retain before terminating your Account. Provisions
            that by their nature should survive termination shall survive, including ownership provisions, warranty disclaimers,
            indemnity, and limitations of liability.
          </p>
        </section>

        <section className="my-4">
          <h2>16. Data Retention and Deletion</h2>
          <p>
            We will retain your Customer Data for as long as your Account is active and for a reasonable period thereafter
            to comply with legal obligations, resolve disputes, and enforce our agreements. You may request deletion of your
            data by contacting us, subject to our legal retention requirements.
          </p>
          <p>
            Following termination, we may retain limited information as necessary for legitimate business purposes and legal
            compliance. Deleted data cannot be recovered.
          </p>
        </section>

        <section className="my-4">
          <h2>17. Dispute Resolution and Governing Law</h2>
          <h3>17.1 Informal Resolution</h3>
          <p>
            Before filing a claim, you agree to contact us and attempt to resolve the dispute informally by sending a written
            notice describing the dispute and your desired resolution to info@thedefinedcrm.com. We will attempt to resolve
            the dispute within 60 days.
          </p>

          <h3>17.2 Governing Law</h3>
          <p>
            These Terms shall be governed by and construed in accordance with the laws of the State of [Your State], without
            regard to its conflict of law provisions. The United Nations Convention on Contracts for the International Sale
            of Goods shall not apply.
          </p>

          <h3>17.3 Jurisdiction and Venue</h3>
          <p>
            Any legal action or proceeding arising out of or related to these Terms shall be brought exclusively in the
            federal or state courts located in [Your County, State]. You consent to the personal jurisdiction of such courts
            and waive any objection to venue.
          </p>

          <h3>17.4 Class Action Waiver</h3>
          <p>
            You agree that any dispute resolution proceedings will be conducted only on an individual basis and not in a
            class, consolidated, or representative action. You waive any right to participate in a class action lawsuit or
            class-wide arbitration.
          </p>
        </section>

        <section className="my-4">
          <h2>18. Changes to Terms</h2>
          <p>
            We reserve the right to modify these Terms at any time. We will provide notice of material changes by posting
            the updated Terms on our website and updating the "Last updated" date. For material changes, we may also send
            email notification to the address associated with your Account.
          </p>
          <p>
            Your continued use of the Service after the effective date of the updated Terms constitutes your acceptance of
            the changes. If you do not agree to the modified Terms, you must stop using the Service and may cancel your
            subscription.
          </p>
        </section>

        <section className="my-4">
          <h2>19. Export Compliance</h2>
          <p>
            The Service may be subject to U.S. export control laws and regulations. You agree to comply with all applicable
            export and re-export control laws and regulations, including the Export Administration Regulations and trade and
            economic sanctions maintained by the Office of Foreign Assets Control. You represent that you are not located in,
            under the control of, or a national or resident of any restricted country.
          </p>
        </section>

        <section className="my-4">
          <h2>20. Government Users</h2>
          <p>
            If you are a U.S. government entity or using the Service on behalf of a U.S. government entity, the Service is a
            "Commercial Item" as defined in 48 C.F.R. §2.101, and is provided with only those rights as are granted to all
            other users under these Terms.
          </p>
        </section>

        <section className="my-4">
          <h2>21. Assignment</h2>
          <p>
            You may not assign or transfer these Terms or your rights and obligations hereunder without our prior written
            consent. We may assign these Terms without restriction. Any attempted assignment in violation of this section
            shall be void.
          </p>
        </section>

        <section className="my-4">
          <h2>22. Force Majeure</h2>
          <p>
            We shall not be liable for any failure or delay in performance due to circumstances beyond our reasonable control,
            including acts of God, war, terrorism, riots, embargoes, acts of civil or military authorities, fire, floods,
            accidents, network infrastructure failures, strikes, or shortages of transportation facilities, fuel, energy,
            labor, or materials.
          </p>
        </section>

        <section className="my-4">
          <h2>23. Severability</h2>
          <p>
            If any provision of these Terms is found to be invalid, illegal, or unenforceable, the remaining provisions shall
            continue in full force and effect. The invalid, illegal, or unenforceable provision shall be deemed modified to
            the minimum extent necessary to make it valid, legal, and enforceable.
          </p>
        </section>

        <section className="my-4">
          <h2>24. Waiver</h2>
          <p>
            No waiver of any term or condition of these Terms shall be deemed a further or continuing waiver of such term or
            condition or any other term or condition. Our failure to assert any right or provision under these Terms shall
            not constitute a waiver of such right or provision.
          </p>
        </section>

        <section className="my-4">
          <h2>25. Entire Agreement</h2>
          <p>
            These Terms, together with our Privacy Policy and any other legal notices or policies published by us on the
            Service, constitute the entire agreement between you and us regarding the Service and supersede all prior or
            contemporaneous communications, whether electronic, oral, or written, between you and us regarding the Service.
          </p>
        </section>

        <section className="my-4">
          <h2>26. Contact Information</h2>
          <p>
            For questions, complaints, or claims regarding these Terms of Service, please contact us at:
          </p>
          <p>
            <strong>Defined Home Services LLC</strong><br />
            Email: info@thedefinedcrm.com<br />
            Address: [Your Physical Address]<br />
            Phone: [Your Phone Number]
          </p>
          <p>
            We will respond to all inquiries within a reasonable timeframe.
          </p>
        </section>

        <section className="my-4">
          <p className="text-muted small">
            By using the Service, you acknowledge that you have read these Terms of Service, understand them, and agree to
            be bound by them. If you do not agree to these Terms, you are not authorized to use the Service.
          </p>
        </section>
      </Container>
      <Footer />
    </>
  );
};

export default Terms;
